import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { Menu, type MenuItem } from '../../components/Menu';
import { ErrorNotice } from '../../components/States';
import { useToast } from '../../components/Toast';
import { deletePost, type Post } from '../../lib/api';
import { useRefreshBoards } from '../feed/queries';
import { BlockDialog } from './BlockDialog';
import { ReportDialog } from './ReportDialog';
import styles from './Safety.module.css';

export function PostMenu({ post, onEdit }: { post: Post; onEdit?: () => void }) {
  const [dialog, setDialog] = useState<'report' | 'block' | 'delete' | null>(null);
  const toast = useToast();
  const refresh = useRefreshBoards();
  const navigate = useNavigate();
  const name = post.author.name ?? 'this neighbor';

  const remove = useMutation({
    mutationFn: () => deletePost(post.id),
    onSuccess: async () => {
      setDialog(null);
      toast.show('Note deleted');
      await refresh();
      navigate('/');
    },
  });

  const items: MenuItem[] = post.isOwn
    ? [
        ...(onEdit ? [{ label: 'Edit note', icon: 'pencil' as const, onSelect: onEdit }] : []),
        { label: 'Delete note', icon: 'close' as const, danger: true, onSelect: () => setDialog('delete') },
      ]
    : [
        { label: 'Report note', icon: 'flag' as const, onSelect: () => setDialog('report') },
        { label: `Block ${name}`, icon: 'block' as const, danger: true, onSelect: () => setDialog('block') },
      ];

  return (
    <>
      <Menu label="Note actions" items={items} />
      {post.isOwn ? (
        <Dialog open={dialog === 'delete'} onClose={() => setDialog(null)} title="Delete this note?">
          <p>It will disappear from your neighbors' boards. Replies stay under a “deleted” placeholder.</p>
          {remove.error && <ErrorNotice error={remove.error} />}
          <div className={styles.actions}>
            <Button variant="quiet" onClick={() => setDialog(null)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
              Delete note
            </Button>
          </div>
        </Dialog>
      ) : (
        <>
          <ReportDialog
            open={dialog === 'report'}
            onClose={() => setDialog(null)}
            target={{ postId: post.id }}
            subject="this note"
          />
          <BlockDialog open={dialog === 'block'} onClose={() => setDialog(null)} userId={post.author.id} name={name} />
        </>
      )}
    </>
  );
}
